import { IconKey } from "@/assets/icons/geist/IconKey";
import { Button } from "@/components/flexkit/Button";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { formatError } from "@/utils/errors";
import { ListSSOLoginsRequest, type ListSSOLoginsResponse_Login } from "gitpod-next-api/gitpod/v1/account_pb";
import { useCallback, useMemo, useState, type FC, type FormEvent } from "react";
import { Code } from "@connectrpc/connect";

type Props = {
    onBack: () => void;
    returnTo: string;
};

export const ContinueWithSSOForm: FC<Props & PropsWithClassName> = ({ onBack, className, returnTo }) => {
    const api = useGitpodAPI();

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [logins, setLogins] = useState<ListSSOLoginsResponse_Login[]>([]);

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (!email) {
                return;
            }
            setSubmitting(true);
            setLogins([]);
            setEmailError("");

            try {
                const response = await api.accountService.listSSOLogins(
                    new ListSSOLoginsRequest({
                        email,
                        returnTo: returnTo !== "/" ? returnTo : undefined,
                    }),
                );

                if (!response.logins.length) {
                    setEmailError(
                        "We could not find any SSO configurations for your email address. Try another email or contact your administrator.",
                    );
                    return;
                }

                if (response.logins.length === 1) {
                    window.location.assign(response.logins[0].loginUrl);
                    return;
                }

                setLogins(response.logins);
            } catch (error) {
                setEmailError(
                    formatError(error, {
                        [Code.NotFound]:
                            "We did not find an organization with a valid configuration for your email address. To configure SSO sign-in, you first need to sign-in using one of the public providers and then configure SSO.",
                    }),
                );
            } finally {
                setSubmitting(false);
            }
        },
        [api.accountService, email, returnTo],
    );

    const showLoginChooser = logins.length > 1;

    const loginOptions = useMemo(
        () =>
            logins.map((login, index) => ({
                ...login,
                badge: {
                    initials: getInitials(login.displayName),
                    color: badgeColorForIndex(index, login.displayName),
                },
            })),
        [logins],
    );

    if (showLoginChooser) {
        return (
            <div className={cn("space-y-2", className)}>
                <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1 md:max-h-96">
                    {loginOptions.map((login) => (
                        <div
                            key={`${login.loginUrl}-${login.displayName}`}
                            className="flex items-center justify-between rounded-2xl border border-white/20 bg-white/80 px-4 py-3 shadow-sm backdrop-blur"
                        >
                            <div className="flex items-center gap-3 text-left">
                                <span
                                    className={cn(
                                        "flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold uppercase",
                                        login.badge.color,
                                    )}
                                    aria-hidden="true"
                                >
                                    {login.badge.initials}
                                </span>
                                <div>
                                    <p className="font-medium text-gray-900">{login.displayName}</p>
                                </div>
                            </div>
                            <Button
                                data-track-label="true"
                                size="sm"
                                variant="secondary"
                                onClick={() => {
                                    window.location.assign(login.loginUrl);
                                }}
                                data-tracking-id="continue-with-sso-login-option"
                            >
                                Continue
                            </Button>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col gap-2">
                    <Button
                        data-track-label="true"
                        size="lg"
                        variant="link"
                        className="w-full"
                        onClick={() => setLogins([])}
                        data-tracking-id="use-different-email-continue-with-sso-form"
                    >
                        Use a different email
                    </Button>
                    <Button
                        data-track-label="true"
                        size="lg"
                        variant="link"
                        className="w-full"
                        onClick={onBack}
                        data-tracking-id="back-continue-with-sso-login-options"
                    >
                        Back
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <form id="custom-sso-form" onSubmit={handleSubmit} className={cn(className)}>
            <InputField label="Email address" id={"email"} className="text-center" error={emailError}>
                <Input
                    id={"email"}
                    placeholder="Enter your email address"
                    type="email"
                    value={email}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError("");
                        setLogins([]);
                    }}
                />
            </InputField>
            <Button
                data-track-label="true"
                variant="secondary"
                LeadingIcon={IconKey}
                className="w-full"
                disabled={!email || !!emailError || submitting}
                type="submit"
                data-tracking-id="confirm-continue-with-sso-form"
            >
                <span className="text-center">Continue with SSO</span>
            </Button>
            <Button
                data-track-label="true"
                size="lg"
                variant={"link"}
                className="w-full"
                onClick={onBack}
                data-tracking-id="back-continue-with-sso-form"
            >
                <span>Back</span>
            </Button>
        </form>
    );
};

const BADGE_COLORS = [
    "bg-purple-100 text-purple-700",
    "bg-blue-100 text-blue-700",
    "bg-pink-100 text-pink-600",
    "bg-emerald-100 text-emerald-700",
    "bg-amber-100 text-amber-700",
];

const getInitials = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
        return "?";
    }

    const parts = trimmed.split(/\s+/).slice(0, 2);
    return parts.map((part) => part[0]?.toUpperCase()).join("");
};

const badgeColorForIndex = (index: number, seed: string) => {
    const normalizedSeed = seed || String(index);
    const hash = normalizedSeed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return BADGE_COLORS[hash % BADGE_COLORS.length];
};
